import { Col, Row, Spin } from 'antd';
import { capitalize } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { STANDARD_VEHICLES_API } from '../../api/cars';
import useInfiniteQuery from '../../hooks/queries/useInfiniteQuery';
import useDebouncedState from '../../hooks/useDebouncedState';
import Select from '../Select';

const buildLabel = v =>
  `${v.year} ${capitalize(v.make)} ${capitalize(v.model)}${v.style ? ` ${v.style}` : ''}`;

const StandardVehicleLookupSelect = props => {
  const {
    onChange,
    initialVehicleId,
    initialMake,
    initialModel,
    initialStyle,
    initialYear,
    disabled,
    ...rest
  } = props;

  const [searchTerm, setSearchTerm] = useDebouncedState(null, 300);
  const [resolvedInitialVehicleKey, setResolvedInitialVehicleKey] = useState(null);

  const initialVehicleKey = useMemo(() => {
    if (initialVehicleId) return initialVehicleId;
    if (!initialMake || !initialModel || !initialYear) return null;

    return [initialMake, initialModel, initialYear, initialStyle].filter(Boolean).join(':');
  }, [initialMake, initialModel, initialStyle, initialVehicleId, initialYear]);

  const isLookingForInitial =
    !!initialVehicleKey && resolvedInitialVehicleKey !== initialVehicleKey;

  const { query: vehiclesQuery, flatPages } = useInfiniteQuery(
    {
      queryKey: ['standardVehicles'],
      queryFn: ({ pageParam = 1 }) =>
        STANDARD_VEHICLES_API.fetchStandardVehicles({ pageNumber: pageParam, pageSize: 25 }),
    },
    'totalCount',
    'documents',
  );

  useEffect(() => {
    if (!initialVehicleKey || !flatPages.length) return;
    if (resolvedInitialVehicleKey === initialVehicleKey) return;

    const match = flatPages.find(
      v =>
        v._id === initialVehicleId ||
        (v.make === initialMake &&
          v.model === initialModel &&
          String(v.year) === String(initialYear) &&
          (!initialStyle || v.style === initialStyle)),
    );

    if (match) {
      setResolvedInitialVehicleKey(initialVehicleKey);
      if (typeof onChange === 'function') onChange(match._id, match);
    } else if (!vehiclesQuery.hasNextPage && !vehiclesQuery.isFetchingNextPage) {
      setResolvedInitialVehicleKey(initialVehicleKey);
    } else if (vehiclesQuery.hasNextPage && !vehiclesQuery.isFetchingNextPage) {
      vehiclesQuery.fetchNextPage();
    }
  }, [
    flatPages,
    initialMake,
    initialModel,
    initialStyle,
    initialVehicleId,
    initialVehicleKey,
    initialYear,
    onChange,
    resolvedInitialVehicleKey,
    vehiclesQuery.fetchNextPage,
    vehiclesQuery.hasNextPage,
    vehiclesQuery.isFetchingNextPage,
  ]);

  const options = useMemo(() => {
    const filtered = searchTerm
      ? flatPages.filter(v => buildLabel(v).toLowerCase().includes(searchTerm.toLowerCase()))
      : flatPages;

    return filtered.map(v => ({
      value: v._id,
      label: buildLabel(v),
    }));
  }, [flatPages, searchTerm]);

  const handleChange = useCallback(
    value => {
      if (typeof onChange !== 'function') return;
      const vehicle = flatPages.find(v => v._id === value);
      onChange(value, vehicle);
    },
    [flatPages, onChange],
  );

  const handlePopupScroll = useCallback(
    e => {
      const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 1;
      if (bottom && !vehiclesQuery.isFetchingNextPage && vehiclesQuery.hasNextPage) {
        vehiclesQuery.fetchNextPage();
      }
    },
    [vehiclesQuery.fetchNextPage, vehiclesQuery.hasNextPage, vehiclesQuery.isFetchingNextPage],
  );

  return (
    <Select
      {...rest}
      showSearch
      resetOnNoMatch={false}
      allowClear={false}
      disabled={disabled || isLookingForInitial}
      loading={vehiclesQuery.isFetchingNextPage || isLookingForInitial}
      options={options}
      onChange={handleChange}
      onSearch={setSearchTerm}
      onPopupScroll={handlePopupScroll}
      notFoundContent={
        vehiclesQuery.isFetching ? (
          <Row justify="center" align="middle">
            <Col>
              <Spin size="default" />
            </Col>
          </Row>
        ) : undefined
      }
    />
  );
};

export default StandardVehicleLookupSelect;
